import type { GetExpensesQuery, Maybe } from "@src/__generated__/graphql";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { MediaModel } from "@src/utils/components/dropzone";
import { denominate } from "@src/utils/formatters";
import { map } from "lodash";

type TExpense = NonNullable<GetExpensesQuery["expenses"]>["data"][0];
type TProject = NonNullable<TExpense["projects"]>[0];
type TContractor = NonNullable<TExpense["contractor"]>;

export class ExpenseModel {
  id: TExpense["id"];
  invoice_status: TExpense["invoice_status"];
  status: TExpense["status"];
  type: TExpense["type"];
  abraflexi_exported: TExpense["abraflexi_exported"];
  xero_exported: TExpense["xero_exported"];
  contractor?: ContractorModel;
  variable_symbol: TExpense["variable_symbol"];
  iban: TExpense["iban"];
  amount: TExpense["stats"]["total_amount"];
  total_amount_without_vat_in_currency: TExpense["stats"]["total_amount_without_vat_in_currency"];
  total_amount_with_vat_in_currency: TExpense["stats"]["total_amount_with_vat_in_currency"];
  currency: TExpense["currency"];
  userCreatedDate: Date | null;
  date_of_supply: Date | null;
  due_date: Date | null;
  paid_at: Date | null;
  projects: ProjectsModel[];
  hasMasterAgreement: TExpense["has_master_agreement"];
  is_draft: TExpense["is_draft"];
  documentExports: TExpense["documentExports"];
  expense_documents: MediaModel[];
  expense_no: TExpense["expense_no"];
  expenseItems: TExpense["expenseItems"];
  workspaceBankAccountId: TExpense["workspace_bank_account_id"];
  note: TExpense["note"];
  internal_note: TExpense["internal_note"];
  workspaceCompanyName: string | undefined;
  accountMoves: Maybe<TExpense["accountMoves"]>;
  exchange_rate: NonNullable<TExpense["exchange_rate"]>;
  paid: number | undefined;
  projectTimeBilling?: { id: string; is_locked: boolean };
  modelLock: TExpense["modelLock"];

  constructor(src: TExpense) {
    this.id = src.id;
    this.invoice_status = src.invoice_status;
    this.status = src.status;
    this.type = src.type;
    this.abraflexi_exported = src.abraflexi_exported;
    this.hasMasterAgreement = src.has_master_agreement;
    this.contractor = src.contractor
      ? new ContractorModel(src.contractor)
      : undefined;
    this.variable_symbol = src.variable_symbol;
    this.iban = src.iban;
    this.amount = src.stats.total_amount;
    this.total_amount_with_vat_in_currency =
      src.stats.total_amount_with_vat_in_currency;
    this.total_amount_without_vat_in_currency =
      src.stats.total_amount_without_vat_in_currency;
    this.currency = src.currency;
    this.projects = map(src.projects, (p) => new ProjectsModel(p));
    this.documentExports = src.documentExports;
    this.paid_at = src.paid_at ? new Date(src.paid_at) : null;
    this.userCreatedDate = src.user_created_date
      ? new Date(src.user_created_date)
      : null;
    this.date_of_supply = src.date_of_supply
      ? new Date(src.date_of_supply)
      : null;
    this.due_date = src.due_date ? new Date(src.due_date) : null;
    this.is_draft = src.is_draft;
    this.expense_documents = src.expense_documents.map(
      (i) => new MediaModel(i),
    );
    this.expense_no = src.expense_no;
    this.expenseItems = src.expenseItems;
    this.xero_exported = src.xero_exported;
    this.note = src.note;
    this.internal_note = src.internal_note;
    this.workspaceBankAccountId = src.workspace_bank_account_id;
    this.accountMoves = src.accountMoves;
    this.exchange_rate =
      src.exchange_rate ?? denominate(1, EXCHANGE_RATE_DENOMINATION);
    this.paid =
      Array.isArray(src.accountMoves) && src.accountMoves.length > 0
        ? src.accountMoves?.reduce((prev, curr) => {
            return prev + curr.amount_in_move_currency;
          }, 0)
        : undefined;
    this.projectTimeBilling = src.projectTimeBilling ?? undefined;
    this.modelLock = src.modelLock;

    if (src.workspace_company_id) {
      this.workspaceCompanyName = appStore.workspaceStore.getCompany(
        src.workspace_company_id,
      )?.name;
    }
  }
}

class ContractorModel {
  id: TContractor["id"];
  legal_name: TContractor["legal_name"];
  constructor(src: TContractor) {
    this.id = src.id;
    this.legal_name = src.legal_name;
  }
}

class ProjectsModel {
  id: TProject["id"];
  title: TProject["title"];
  code: TProject["code"];
  constructor(src: TProject) {
    this.id = src.id;
    this.title = src.title;
    this.code = src.code;
  }
}
